import { Fragment, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, Loader2, Search, Stethoscope, CircleAlert } from 'lucide-react'
import { apiGet, cachedGet } from '../../../lib/api.js'
import { apiErrorText } from '../../../lib/apiError.js'
import { usePersistedState } from '../../../lib/persist.js'
import { relabelBranch } from '../../../lib/asset.js'
import { pickableBranches, HQ_BRANCH_ID, FALLBACK_BRANCH_ID } from '../../../lib/branch.js'
import { apptStatusInfo } from '../../../data/appointmentStatuses.js'
import { STATUS_TONES, statusTone } from '../../../data/patientStatuses.js'
import {
  MATURITY_DAYS, MATURITY_CHOICES, STATUS, STATUS_ORDER, deriveStatus, summarize,
  groupConsults, CONSULT_GROUPERS, bkkTodayISO, fmtDay, NOSHOW_NOTE, WON_KIND,
  callState, HOLD_TYPE_LABEL,
} from '../../../lib/implant.js'
import ResultPill from './ResultPill.jsx'
import StarChart from '../../../components/StarChart.jsx'
import { Button } from '../../../kit'
import PatientLink from '../../../components/PatientLink.jsx'
import { Card, DateRange, Segmented, Notice, fmt, INPUT, asList } from '../../statistic/statShared.jsx'

/* ────────────────────────────────────────────────────────────────────────────
 * TAB · ผลเคสปรึกษา (was ติดตามเคสปรึกษา — renamed 2026-10-30, Ram: it is a REPORT of
 * where each consult went, not a list to call from; calling happens only in รอโทร).
 *
 * ปิดได้ = a CLOSING bill — implant, denture or bridge — the same rule as Warm Case
 * (api 651 since 2026-10-30). A paid Stent / graft / sinus lift / Digital Guide alone
 * or a booked next step is เตรียมตัว (they decided; the clinic is preparing). A win says
 * what was bought, and the rate tile splits the wins into รากเทียม vs ฟันปลอม / สะพานฟัน so
 * the implant share stays readable. ยังไม่ตัดสินใจ / หลุด / ไม่มาตามนัด rows say what the
 * calls found and where the patient is now (callState: api 678's parking on api 651's
 * facts) — the report and รอโทร tell one story. เปลี่ยนไปทำอย่างอื่น is gone (2026-10-30: it
 * read the consult's own fee / X-ray / CT as other treatment on 94% of consults).
 *
 * Source: GET /api/implant/consult → Xano /report/implant_consult (api 651).
 * The endpoint returns FACTS; src/lib/implant.js turns them into the Thai
 * status, so the 180-day constant lives in one unit-tested place.
 *
 * 🚨 THE THREE THINGS THIS PAGE MUST NOT SAY
 *
 * 1. It must not present ไม่มาตามนัด as a recorded fact. `appointment.status` is
 *    an enum of C, W, P, U, X, C*, P*, R48 — there is no no-show value, and
 *    measured against 822,851 rows there are zero. Every such cell carries
 *    (คำนวณ) and the NOSHOW_NOTE tooltip.
 *
 * 2. It must not call a recent consult หลุด. 82% of everyone who buys takes
 *    longer than two weeks, so an immature cohort has a guaranteed loss verdict
 *    under any matured-window rule — which is exactly the artifact that produced
 *    the retracted "collapsing conversion" finding. The rate tile prints its own
 *    denominator so the exclusion is visible rather than implied.
 *
 * 3. It must not be read as "the funnel". Recorded implant consults fell
 *    142 → 93 → 70 across Q1 2024-26 WHILE implant revenue rose, because staff
 *    increasingly sell implants during an ordinary visit and never book a
 *    type-35. (The permanent notice saying so was removed on Ram's call,
 *    2026-10-30 — the caveat now lives here and in implant/report.md.)
 * ──────────────────────────────────────────────────────────────────────────── */

const MAX_DAYS = 731     // matches api 651's precondition
const DEFAULT_BACK = 365 // a year, so at least one matured cohort is on screen

const GROUP_OPTS = Object.entries(CONSULT_GROUPERS).map(([v, g]) => ({ v, label: g.noun }))

const TONE_PILL = {
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber:   'bg-amber-50 text-amber-700 ring-amber-200',
  rose:    'bg-rose-50 text-rose-700 ring-rose-200',
  sky:     'bg-sky-50 text-sky-700 ring-sky-200',
  violet:  'bg-violet-50 text-violet-700 ring-violet-200',
  slate:   'bg-slate-100 text-slate-600 ring-slate-200',
}

function Select({ value, onChange, children, className = '' }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} ${className}`}>
      {children}
    </select>
  )
}

/* Status colours, in STATUS_ORDER — the star chart's spoke dots and the rate tile's bar
 * (the outcome bar became a star chart, 2026-10-30). Same colour families as TONE_PILL so
 * the chart and the pills in the drill-down speak one language; ปิดได้ is the darkest
 * step because it is the point. The two slates are deliberate neutrals ("no
 * outcome"), not series hues. Validated light-mode against white, adjacent pairs:
 * CVD ΔE ≥ 11.7, normal-vision ΔE ≥ 18 — only the slates fail the chroma floor.
 * prep (เตรียมตัว, 2026-10-30) is violet between won_late and open: re-validated
 * with it in place, worst adjacent CVD ΔE 9.2 (violet ↔ sky, deutan), normal ΔE ≥ 19.3.
 * ยกเลิกล่วงหน้า (rose) folded into ไม่มาตามนัด (2026-10-30): lost and upcoming now sit
 * side by side — re-validated, normal ΔE 15.6 (≥ 15), CVD worst still 9.2. */
const OUTCOME_FILL = {
  won: '#047857', won_late: '#10b981', prep: '#7c3aed', open: '#0284c7', noshow: '#d97706',
  lost: '#64748b', upcoming: '#94a3b8',
}

/* Under the status pill: เตรียมตัว says which step (booked / paid); an open, lost or
 * no-show consult says what the latest call found and where the patient is now. A call
 * made before this consult is not shown here — it is about an earlier round. */
const STATE_TONE = {
  queue: 'text-brand-700', queue_new: 'text-brand-700', parked: 'text-amber-700', retry: 'text-amber-700',
  waiting: 'text-violet-700', out: 'text-slate-500', off: 'text-slate-400', cancelled: 'text-rose-600',
}
function OutcomeDetail({ r, code, today }) {
  if (code === 'prep') {
    return (
      <span className="mt-0.5 block text-[11px] text-slate-500">
        {r.booked_date && <span className="block">นัด{HOLD_TYPE_LABEL[r.booked_type] || 'ขั้นต่อไป'} {fmtDay(r.booked_date)}</span>}
        {r.prep_date && <span className="block">จ่าย{r.prep_name || 'ขั้นเตรียม'} {fmtDay(r.prep_date)}</span>}
      </span>
    )
  }
  if (!['open', 'lost', 'noshow'].includes(code)) return null
  const st = callState(r, today)
  const since = Date.parse(`${String(r.date).slice(0, 10)}T00:00:00+07:00`)
  const called = r.p_call_code && Number(r.p_call_at) >= since
  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
      {called && (
        <>
          <ResultPill small code={r.p_call_code} />
          <span className="tabular-nums text-slate-400">{fmtDay(new Date(Number(r.p_call_at) + 7 * 3600000).toISOString().slice(0, 10))}</span>
          <span className="text-slate-300">·</span>
        </>
      )}
      <span className={STATE_TONE[st.code] || 'text-slate-500'}>
        {st.label}{st.until ? ` ${fmtDay(st.until)}` : ''}
      </span>
    </span>
  )
}

function Panel({ label, children, className = '' }) {
  return (
    <div className={`rounded-xl border border-slate-100 bg-slate-50/60 p-4 ${className}`}>
      {label && <div className="text-[12px] font-medium text-slate-500">{label}</div>}
      {children}
    </div>
  )
}

/* ผลของนัดปรึกษา as a star chart (Ram, 2026-10-30) — the sibling of ผลการโทร's: one point
 * per status, in STATUS_ORDER, each spoke's dot in the status colour (OUTCOME_FILL);
 * hover shows the share. Only the statuses that have consults — the same set as the
 * table below (Ram, 2026-10-30) — unless fewer than 3 do: a star needs 3 spokes, so
 * then every status shows. A point opens that status in the table below. */
function OutcomeStar({ counts, total, onPick }) {
  const all = STATUS_ORDER.map((c) => ({
    key: c, label: STATUS[c].label, value: counts[c] || 0, color: OUTCOME_FILL[c],
  }))
  const present = all.filter((a) => a.value > 0)
  const axes = present.length >= 3 ? present : all
  return (
    <div className="mt-5">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="text-[12px] font-medium text-slate-500">ผลของนัดปรึกษา</span>
        <span className="truncate text-[12px] text-slate-400">ทั้งหมด {fmt(total)} นัด · กดจุดเพื่อดูรายชื่อ</span>
      </div>
      <StarChart axes={axes} unit="นัด" height={300} emptyText="ไม่มีนัดปรึกษาในช่วงนี้" onPick={onPick} />
      <p className="mt-1 flex items-start gap-1 text-[11.5px] text-amber-700">
        <CircleAlert size={12} className="mt-[2px] shrink-0" />{NOSHOW_NOTE}
      </p>
    </div>
  )
}

export default function ConsultTrack() {
  // Bangkok's today, never the browser's — a viewer abroad must not be offered a
  // day the query would refuse. Commit 6b7bb88 exists for exactly this.
  const today = useMemo(() => bkkTodayISO(), [])
  const defaultStart = useMemo(
    () => new Date(Date.parse(`${today}T00:00:00Z`) - DEFAULT_BACK * 86400000).toISOString().slice(0, 10),
    [today],
  )

  const [start, setStart] = usePersistedState('imp:cStart', defaultStart)
  const [end, setEnd] = usePersistedState('imp:cEnd', today)
  const [branchSaved, setBranchSel] = usePersistedState('imp:cBranch', '')
  // A browser that saved สนามเป้า before it left the list opens on อารีย์ instead.
  const branchSel = Number(branchSaved) === HQ_BRANCH_ID ? String(FALLBACK_BRANCH_ID) : branchSaved
  const [maturityRaw, setMaturity] = usePersistedState('imp:maturity', MATURITY_DAYS)
  const maturity = MATURITY_CHOICES.includes(Number(maturityRaw)) ? Number(maturityRaw) : MATURITY_DAYS
  const [groupByRaw, setGroupBy] = usePersistedState('imp:cGroup', 'status')
  const groupBy = CONSULT_GROUPERS[groupByRaw] ? groupByRaw : 'status'

  const [branches, setBranches] = useState([])
  const [raw, setRaw] = useState(null)
  const [st, setSt] = useState({ loading: true, err: '' })
  // which groups are open — any number (Ram, 2026-10-30: ขยาย / ย่อทั้งหมด)
  const [openGroups, setOpenGroups] = useState(() => new Set())
  const toggleGroup = (k) => setOpenGroups((cur) => {
    const next = new Set(cur)
    if (next.has(k)) next.delete(k); else next.add(k)
    return next
  })

  useEffect(() => {
    cachedGet('/api/branches')
      // สนามเป้า (1, closed head office) is not offered — อารีย์ (8) covers it; the
      // Xano query matches both ids when 8 is picked (2026-09-17, Ram).
      .then((r) => setBranches(pickableBranches(asList(r).map((b) => ({
        id: String(b.id ?? b.branch_id),
        name: relabelBranch(b.name || b.branch_name || `สาขา #${b.id}`),
      })))))
      .catch(() => {})
  }, [])

  useEffect(() => {
    let alive = true
    setSt({ loading: true, err: '' })
    setOpenGroups(new Set())
    apiGet('/api/implant/consult', { start_date: start, end_date: end, branch_id: branchSel || 0 })
      .then((r) => { if (alive) { setRaw(r); setSt({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setRaw(null); setSt({ loading: false, err: apiErrorText(e, 'โหลดข้อมูลไม่สำเร็จ') }) } })
    return () => { alive = false }
  }, [start, end, branchSel])

  const rows = useMemo(() => asList(raw?.rows), [raw])
  const s = useMemo(() => summarize(rows, today, maturity), [rows, today, maturity])
  const groups = useMemo(() => groupConsults(rows, groupBy, today, maturity), [rows, groupBy, today, maturity])
  useEffect(() => { setOpenGroups(new Set()) }, [groupBy, maturity])
  // a point on the star chart opens that status in the table below
  const pickStatus = (code) => {
    if (groupBy !== 'status') setGroupBy('status')
    setOpenGroups(new Set([code]))
    setTimeout(() => document.getElementById('consult-groups')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
  }

  const noun = CONSULT_GROUPERS[groupBy].noun
  const conv = s.conversion

  return (
    <>
      <Card
        icon={<Search size={18} />}
        title="ค้นหา / กรอง"
        subtitle="ช่วงวันนัด · สาขา · กรอบเวลาที่ถือว่าปิดได้ · จัดกลุ่มตาม"
      >
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ช่วงวันนัด</label>
            <DateRange start={start} end={end} today={today} onStart={setStart} onEnd={setEnd} maxDays={MAX_DAYS} />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">สาขา</label>
            <Select value={branchSel} onChange={setBranchSel} className="min-w-[160px]">
              <option value="">ทุกสาขา</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">นับว่าปิดได้ภายใน</label>
            <Segmented value={maturity} onChange={setMaturity}
              options={MATURITY_CHOICES.map((d) => ({ v: d, label: `${d} วัน` }))} />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">จัดกลุ่มตาม</label>
            <Segmented value={groupBy} onChange={setGroupBy} options={GROUP_OPTS} />
          </div>
        </div>
        {maturity !== MATURITY_DAYS && (
          <p className="mt-2 text-[12px] text-amber-700">
            180 วันคือกรอบเดียวที่วัดมาแล้ว — คนไข้ 82% ที่ตัดสินใจทำ ใช้เวลานานกว่า 2 สัปดาห์
            การใช้กรอบสั้นกว่านี้จะนับเคสที่ปิดได้จริงเป็น “หลุด”
          </p>
        )}
      </Card>

      {st.err ? (
        <div className="mt-4"><Notice tone="error" text={`โหลดข้อมูลไม่สำเร็จ: ${st.err}`} /></div>
      ) : st.loading ? (
        <div className="mt-4">
          <Card icon={<Stethoscope size={18} />} title="กำลังโหลด…">
            <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[200px]">
              <Loader2 size={16} className="animate-spin" /> กำลังโหลดนัดปรึกษารากเทียม…
            </div>
          </Card>
        </div>
      ) : (
        <>
          <div className="mt-4">
            <Card icon={<Stethoscope size={18} />} title="สรุป"
              subtitle="อัตราปิดคิดรายคนไข้ จากนัดปรึกษาครั้งแรกของแต่ละคนในช่วงที่เลือก">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr]">
                {/* The one hero number. Proportional figures — tabular-nums looks loose at this size. */}
                <Panel label="อัตราปิด" className="sm:col-span-2 lg:col-span-1">
                  <div className="mt-1 text-[48px] font-semibold leading-none tracking-tight text-slate-900">
                    {conv.rate == null ? '—' : `${Math.round(conv.rate * 1000) / 10}%`}
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-[4px] bg-emerald-100">
                    <div className="h-full rounded-[4px]"
                      style={{ width: `${(conv.rate || 0) * 100}%`, background: OUTCOME_FILL.won }} />
                  </div>
                  <p className="mt-2 text-[12px] text-slate-500">
                    {conv.rate == null
                      ? `ยังไม่มีนัดที่ครบ ${maturity} วัน`
                      : <>ปิดได้ <b className="font-semibold text-slate-700">{fmt(conv.won)}</b> จาก {fmt(conv.denom)} คนที่ครบ {maturity} วันแล้ว</>}
                  </p>
                  {conv.won > 0 && (
                    <p className="text-[11.5px] text-slate-500">
                      รากเทียม <b className="font-semibold text-slate-700">{fmt(conv.wonImplant)}</b>
                      {' · '}ฟันปลอม / สะพานฟัน <b className="font-semibold text-slate-700">{fmt(conv.won - conv.wonImplant)}</b>
                    </p>
                  )}
                  {conv.immature > 0 && (
                    <p className="text-[11.5px] text-slate-400">อีก {fmt(conv.immature)} คนยังอยู่ในช่วงติดตาม</p>
                  )}
                </Panel>

                <Panel label="ส่วนคลินิกที่เกิดขึ้น">
                  <div className="mt-1 text-[28px] font-semibold leading-tight text-slate-900">฿{fmt(s.clinic)}</div>
                  <p className="mt-1 text-[12px] text-slate-400">ภายใน {MATURITY_DAYS} วันจากวันนัด</p>
                </Panel>

                <Panel>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[12px] font-medium text-slate-500">นัดปรึกษา</div>
                      <div className="mt-1 text-[28px] font-semibold leading-tight text-slate-900">
                        {fmt(s.consults)} <span className="text-[14px] font-medium text-slate-400">นัด</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[12px] font-medium text-slate-500">คนไข้</div>
                      <div className="mt-1 text-[28px] font-semibold leading-tight text-slate-900">
                        {fmt(s.patients)} <span className="text-[14px] font-medium text-slate-400">คน</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-slate-200/70 pt-2 text-[12px] text-slate-500">
                    มาตามนัด <b className="font-semibold text-slate-700">{fmt(s.attended)}</b> นัด
                  </div>
                </Panel>
              </div>

              <OutcomeStar counts={s.counts} total={s.consults} onPick={pickStatus} />

              <div className="mt-4 flex items-center gap-1.5 border-t border-slate-100 pt-3 text-[12px] text-slate-500">
                <span className={s.noNote ? 'text-rose-500' : 'text-slate-300'}>●</span>
                นัดที่ไม่ได้เขียนโน้ต <b className={`font-semibold ${s.noNote ? 'text-slate-700' : 'text-slate-400'}`}>{fmt(s.noNote)}</b> นัด
                <span className="text-slate-400">· ช่อง details ของนัด</span>
              </div>
            </Card>
          </div>

          {!rows.length ? (
            <div className="mt-4"><Notice tone="warn" text="ไม่พบนัดปรึกษารากเทียมในช่วงและตัวกรองที่เลือก" /></div>
          ) : (
            <div className="mt-4" id="consult-groups">
              <Card icon={<Stethoscope size={18} />} title={`แยกตาม${noun}`} badge={`${fmt(groups.length)} กลุ่ม`}
                subtitle="กดที่แถวเพื่อดูนัดในกลุ่มนั้น"
                right={(
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" icon={ChevronsUpDown}
                      disabled={openGroups.size === groups.length}
                      onClick={() => setOpenGroups(new Set(groups.map((g) => g.key)))}>ขยายทั้งหมด</Button>
                    <Button size="sm" variant="ghost" icon={ChevronsDownUp}
                      disabled={openGroups.size === 0}
                      onClick={() => setOpenGroups(new Set())}>ย่อทั้งหมด</Button>
                  </div>
                )}>
                <div className="overflow-x-auto -mx-1 px-1">
                  <table className="w-full text-[13px] table-fixed">
                    <colgroup><col className="w-[34px]" /><col /><col className="w-[120px]" /><col className="w-[110px]" /></colgroup>
                    <thead>
                      <tr className="text-[11.5px] text-slate-400 border-b border-slate-200">
                        <th></th>
                        <th className="text-left font-medium py-2">{noun}</th>
                        <th className="text-right font-medium">จำนวน (นัด)</th>
                        <th className="text-right font-medium">สัดส่วน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((g) => {
                        const open = openGroups.has(g.key)
                        const isNoshow = groupBy === 'status' && g.key === 'noshow'
                        return (
                          <Fragment key={g.key}>
                            <tr className={`border-b border-slate-100 cursor-pointer ${open ? 'bg-brand-50/40' : 'hover:bg-slate-50/70'}`}
                              onClick={() => toggleGroup(g.key)}>
                              <td className="py-2.5 text-slate-300"><ChevronDown size={16} className={`transition ${open ? '' : '-rotate-90'}`} /></td>
                              <td className="pr-2">
                                <span className="truncate font-medium text-slate-800">{g.label}</span>
                                {isNoshow && (
                                  <span title={NOSHOW_NOTE} className="ml-1.5 text-[11px] text-amber-600 inline-flex items-center gap-0.5">
                                    <CircleAlert size={11} /> (คำนวณ)
                                  </span>
                                )}
                              </td>
                              <td className="text-right tabular-nums font-semibold text-slate-800">{fmt(g.count)}</td>
                              <td className="text-right tabular-nums text-slate-500">{Math.round((g.count / rows.length) * 100)}%</td>
                            </tr>
                            {open && (
                              <tr>
                                <td></td>
                                <td colSpan={3} className="py-2 pr-1">
                                  <ConsultList rows={g.rows} today={today} maturity={maturity} />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })}
                      <tr className="border-t border-slate-200">
                        <td></td>
                        <td className="py-2 text-[12.5px] font-medium text-slate-500">รวม</td>
                        <td className="text-right tabular-nums font-semibold text-slate-900">{fmt(rows.length)}</td>
                        <td className="text-right tabular-nums text-slate-400">100%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </>
  )
}

function ConsultList({ rows, today, maturity }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/40 overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-[11px] text-slate-400 border-b border-slate-200">
            <th className="text-left font-medium py-1.5 pl-3">วันที่นัด</th>
            <th className="text-left font-medium">คนไข้</th>
            <th className="text-left font-medium">สถานะคนไข้</th>
            <th className="text-left font-medium">สาขา</th>
            <th className="text-left font-medium">ที่มา</th>
            <th className="text-left font-medium">สถานะนัด</th>
            <th className="text-left font-medium">ผลที่คำนวณได้</th>
            <th className="text-right font-medium">ส่วนคลินิก</th>
            <th className="text-left font-medium pl-3 pr-3">นัดถัดไป</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const d = deriveStatus(r, today, maturity)
            const ai = apptStatusInfo(r.status)
            const pill = TONE_PILL[d.tone] || TONE_PILL.slate
            const pTone = STATUS_TONES[statusTone(r.patient_status)] || STATUS_TONES.none
            return (
              <tr key={r.id} className="border-b border-slate-100 last:border-0">
                <td className="py-1.5 pl-3 tabular-nums text-slate-600 whitespace-nowrap">{fmtDay(r.date)}</td>
                <td className="pr-2">
                  <PatientLink patientId={r.patient_id} hoverClass="" className="text-slate-700">
                    {/* Bold on hover without the column jumping: an invisible bold copy
                        holds the wider width in the same grid cell at all times. */}
                    <span className="inline-grid">
                      <span className="col-start-1 row-start-1 group-hover:font-semibold group-hover:text-brand-600">{r.name}</span>
                      <span aria-hidden className="invisible col-start-1 row-start-1 font-semibold">{r.name}</span>
                    </span>
                    <span className="text-slate-400 tabular-nums group-hover:text-brand-600"> · {r.hn}</span>
                  </PatientLink>
                  {r.consult_total > 1 && (
                    <span className="ml-1 text-[10.5px] text-slate-400" title="นับเฉพาะในช่วงวันที่ที่เลือก">
                      ปรึกษาครั้งที่ {r.consult_seq}/{r.consult_total}
                    </span>
                  )}
                </td>
                <td className="pr-2">
                  {r.patient_status ? (
                    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10.5px] ring-1 ${pTone.pill}`}
                      title="สถานะคนไข้ปัจจุบัน — ไม่ใช่สถานะ ณ วันนัด">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${pTone.dot}`} />{r.patient_status}
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="text-slate-500 truncate">{relabelBranch(r.branch)}</td>
                <td className="text-slate-500">{r.from || '—'}</td>
                <td>
                  <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] ring-1 ${ai.pill}`}>{ai.abbr}</span>
                </td>
                <td>
                  <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] ring-1 ${pill}`}
                    title={d.code === 'noshow' ? NOSHOW_NOTE : undefined}>
                    {STATUS[d.code].label}{d.code === 'noshow' ? ' (คำนวณ)' : ''}
                    {/* what the win bought — api 651 won_kind (older rows: an implant bill) */}
                    {(d.code === 'won' || d.code === 'won_late') && ` · ${WON_KIND[r.won_kind] || WON_KIND.implant}`}
                  </span>
                  {r.prior_implant && (
                    <span className="ml-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10.5px] text-slate-500"
                      title="คนไข้มีรายการรากเทียมภายใน 1 ปีก่อนวันนัดนี้ — ไม่นับเข้าอัตราปิด">มีรากเทียมอยู่ก่อน</span>
                  )}
                  <OutcomeDetail r={r} code={d.code} today={today} />
                  {!r.has_details && (
                    <span className="ml-1 text-rose-500" title="นัดนี้ไม่ได้เขียนรายละเอียดไว้ — หมอที่รักษาจะไม่เห็นบริบท">●</span>
                  )}
                </td>
                <td className="text-right tabular-nums text-slate-600">{r.won_clinic ? `฿${fmt(r.won_clinic)}` : <span className="text-slate-300">—</span>}</td>
                <td className="pl-3 pr-3 tabular-nums text-slate-500 whitespace-nowrap">{r.next_appt_date ? fmtDay(r.next_appt_date) : <span className="text-slate-300">—</span>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
